export const globals = {
  screens: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },

  colors: {
    transparent: '#00000000',
    current: 'currentColor',

    black: '#000',
    white: '#fff',

    gray: {
      100: '#F7FAFC',
      200: '#EDF2F7',
      300: '#E2E8F0',
      400: '#CBD5E0',
      500: '#A0AEC0',
      600: '#718096',
      700: '#4A5568',
      800: '#2D3748',
      900: '#1A202C',
    },
    red: {
      100: '#FFF5F5',
      200: '#FED7D7',
      300: '#FEB2B2',
      400: '#FC8181',
      500: '#F56565',
      600: '#E53E3E',
      700: '#C53030',
      800: '#9B2C2C',
      900: '#742A2A',
    },
    orange: {
      100: '#FFFAF0',
      200: '#FEEBC8',
      300: '#FBD38D',
      400: '#F6AD55',
      500: '#ED8936',
      600: '#DD6B20',
      700: '#C05621',
      800: '#9C4221',
      900: '#7B341E',
    },
    yellow: {
      100: '#FFFFF0',
      200: '#FEFCBF',
      300: '#FAF089',
      400: '#F6E05E',
      500: '#ECC94B',
      600: '#D69E2E',
      700: '#B7791F',
      800: '#975A16',
      900: '#744210',
    },
    green: {
      100: '#F0FFF4',
      200: '#C6F6D5',
      300: '#9AE6B4',
      400: '#68D391',
      500: '#48BB78',
      600: '#38A169',
      700: '#2F855A',
      800: '#276749',
      900: '#22543D',
    },
    teal: {
      100: '#E6FFFA',
      200: '#B2F5EA',
      300: '#81E6D9',
      400: '#4FD1C5',
      500: '#38B2AC',
      600: '#319795',
      700: '#2C7A7B',
      800: '#285E61',
      900: '#234E52',
    },
    blue: {
      100: '#EBF8FF',
      200: '#BEE3F8',
      300: '#90CDF4',
      400: '#63B3ED',
      500: '#4299E1',
      600: '#3182CE',
      700: '#2B6CB0',
      800: '#2C5282',
      900: '#2A4365',
    },
    indigo: {
      100: '#EBF4FF',
      200: '#C3DAFE',
      300: '#A3BFFA',
      400: '#7F9CF5',
      500: '#667EEA',
      600: '#5A67D8',
      700: '#4C51BF',
      800: '#434190',
      900: '#3C366B',
    },
    purple: {
      100: '#FAF5FF',
      200: '#E9D8FD',
      300: '#D6BCFA',
      400: '#B794F4',
      500: '#9F7AEA',
      600: '#805AD5',
      700: '#6B46C1',
      800: '#553C9A',
      900: '#44337A',
    },
    pink: {
      100: '#FFF5F7',
      200: '#FED7E2',
      300: '#FBB6CE',
      400: '#F687B3',
      500: '#ED64A6',
      600: '#D53F8C',
      700: '#B83280',
      800: '#97266D',
      900: '#702459',
    },
  },

  spacing: {
    px: '1px',
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    32: '8rem',
    40: '10rem',
    48: '12rem',
    56: '15rem',
    64: '16rem',
  },

  opacity: {
    0: '0',
    25: '0.25',
    50: '0.5',
    75: '0.75',
    100: '1',
  },

  duration: {
    75: '75ms',
    100: '100ms',
    150: '150ms',
    200: '200ms',
    300: '300ms',
    500: '500ms',
    700: '700ms',
    1000: '1000ms',
  },

  borderWidth: {
    default: '1px',
    0: '0',
    2: '2px',
    4: '4px',
    8: '8px',
  },
};

const theme = ({
  screens,
  colors,
  spacing,
  opacity,
  duration,
  borderWidth,
}) => ({
  screens: screens,
  colors: colors,
  spacing: spacing,
  backgroundColor: colors,
  gradientColorStops: colors,
  backgroundImage: {
    none: 'none',
    'gradient-to-t': 'linear-gradient(to top, var(--gradient-color-stops))',
    'gradient-to-tr':
      'linear-gradient(to top right, var(--gradient-color-stops))',
    'gradient-to-r': 'linear-gradient(to right, var(--gradient-color-stops))',
    'gradient-to-br':
      'linear-gradient(to bottom right, var(--gradient-color-stops))',
    'gradient-to-b': 'linear-gradient(to bottom, var(--gradient-color-stops))',
    'gradient-to-bl':
      'linear-gradient(to bottom left, var(--gradient-color-stops))',
    'gradient-to-l': 'linear-gradient(to left, var(--gradient-color-stops))',
    'gradient-to-tl':
      'linear-gradient(to top left, var(--gradient-color-stops))',
  },
  backgroundOpacity: opacity,
  // backgroundPosition: position,
  backgroundSize: {
    auto: 'auto',
    cover: 'cover',
    contain: 'contain',
  },
  borderColor: colors,
  borderOpacity: opacity,
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    default: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
  borderWidth: borderWidth,
  boxShadow: {
    xs: '0 0 0 1px rgba(0, 0, 0, 0.05)',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    outline: '0 0 0 3px rgba(66, 153, 225, 0.5)',
    none: 'none',
  },
  // container: {},
  // cursor: x ~> x
  divideColor: colors,
  divideOpacity: opacity,
  divideWidth: borderWidth,
  fill: {
    current: 'currentColor',
  },
  flex: {
    1: '1 1 0%',
    auto: '1 1 auto',
    initial: '0 1 auto',
    none: 'none',
  },
  flexGrow: {
    0: '0',
    default: '1',
  },
  flexShrink: {
    0: '0',
    default: '1',
  },
  fontFamily: {
    sans: '"Lato", Roboto, "Helvetica Neue", "Segoe UI", sans-serif',
    serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
    mono:
      'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '4rem',
  },
  fontWeight: {
    hairline: '100',
    thin: '200',
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },
  fontMetrics: {
    'Segoe UI': {
      capHeight: 1455,
      ascent: 2200,
      descent: -480,
      lineGap: 0,
      unitsPerEm: 2048,
    },
    Roboto: {
      capHeight: 1500,
      ascent: 1900,
      descent: -500,
      lineGap: 0,
      unitsPerEm: 2048,
    },
    'Helvetica Neue': {
      capHeight: 1433,
      ascent: 1974,
      descent: -426,
      lineGap: 0,
      unitsPerEm: 2048,
    },
    Lato: {
      capHeight: 1433,
      ascent: 1974,
      descent: -426,
      lineGap: 0,
      unitsPerEm: 2000,
    },
  },
  height: {
    auto: 'auto',
    ...spacing,
    full: '100%',
    screen: '100vh',
  },
  // inset: x ~> x
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
  },
  // listStyleType: x ~> x
  margin: {
    auto: 'auto',
    ...spacing,
  },
  maxHeight: {
    full: '100%',
    screen: '100vh',
  },
  maxWidth: {
    none: 'none',
    xs: '20rem',
    sm: '24rem',
    md: '28rem',
    lg: '32rem',
    xl: '36rem',
    '2xl': '42rem',
    '3xl': '48rem',
    '4xl': '56rem',
    '5xl': '64rem',
    '6xl': '72rem',
    full: '100%',
    ...Object.entries(screens).reduce(
      (xs, [k, v]) => ({ ...xs, [`screen-${k}`]: v }),
      {}
    ),
  },
  minHeight: {
    0: '0',
    full: '100%',
    screen: '100vh',
  },
  minWidth: {
    0: '0',
    full: '100%',
  },
  // objectPosition: position,
  opacity: opacity,
  order: {
    first: '-9999',
    last: '9999',
    none: '0',
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10',
    11: '11',
    12: '12',
  },
  padding: spacing,
  placeholderColor: colors,
  placeholderOpacity: opacity,
  space: spacing,
  stroke: {
    current: 'currentColor',
  },
  strokeWidth: {
    0: '0',
    1: '1',
    2: '2',
  },
  textColor: colors,
  textOpacity: opacity,
  width: {
    auto: 'auto',
    ...spacing,
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    '1/5': '20%',
    '2/5': '40%',
    '3/5': '60%',
    '4/5': '80%',
    '1/6': '16.666667%',
    '2/6': '33.333333%',
    '3/6': '50%',
    '4/6': '66.666667%',
    '5/6': '83.333333%',
    '1/12': '8.333333%',
    '2/12': '16.666667%',
    '3/12': '25%',
    '4/12': '33.333333%',
    '5/12': '41.666667%',
    '6/12': '50%',
    '7/12': '58.333333%',
    '8/12': '66.666667%',
    '9/12': '75%',
    '10/12': '83.333333%',
    '11/12': '91.666667%',
    full: '100%',
    screen: '100vw',
  },
  zIndex: {
    auto: 'auto',
    0: '0',
    10: '10',
    20: '20',
    30: '30',
    40: '40',
    50: '50',
  },
  gap: spacing,
  // gridTemplateColumns: x ~> x | x ~> repeat(x, minmax(0, 1fr))
  // gridColumn: x ~> span x / span x
  // gridColumnStart: x ~> x
  // gridColumnEnd: x ~> x
  // gridTemplateRows: x ~> x | x ~> repeat(x, minmax(0, 1fr))
  // gridRow: x ~> span x / span x
  // gridRowStart: x ~> x
  // gridRowEnd: x ~> x
  // transformOrigin: position,
  scale: {
    0: '0',
    50: '.5',
    75: '.75',
    90: '.9',
    95: '.95',
    100: '1',
    105: '1.05',
    110: '1.1',
    125: '1.25',
    150: '1.5',
  },
  rotate: {
    0: '0deg',
    45: '45deg',
    90: '90deg',
    180: '180deg',
  },
  translate: {
    ...spacing,
    '-full': '-100%',
    '-1/2': '-50%',
    '1/2': '50%',
    full: '100%',
  },
  skew: {
    0: '0',
    3: '3deg',
    6: '6deg',
    12: '12deg',
  },
  transitionProperty: {
    none: 'none',
    all: 'all',
    default:
      'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
    colors: 'background-color, border-color, color, fill, stroke',
    opacity: 'opacity',
    shadow: 'box-shadow',
    transform: 'transform',
  },
  transitionTimingFunction: {
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    'in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  transitionDuration: duration,
  transitionDelay: duration,
  animation: {
    none: 'none',
    spin: 'spin 1s linear infinite',
    ping: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
    pulse: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    bounce: 'bounce 1s infinite',
  },
  keyframes: {
    spin: {
      to: { transform: 'rotate(360deg)' },
    },
    ping: {
      '75%, 100%': { transform: 'scale(2)', opacity: '0' },
    },
    pulse: {
      '50%': { opacity: '.5' },
    },
    bounce: {
      '0%, 100%': {
        transform: 'translateY(-25%)',
        animationTimingFunction: 'cubic-bezier(0.8,0,1,1)',
      },
      '50%': {
        transform: 'none',
        animationTimingFunction: 'cubic-bezier(0,0,0.2,1)',
      },
    },
  },
});

export default theme;
